/* global $ */

export async function loadMyWorkspaces() {
    const result = await $.post("/query/loadMyWorkspaces", {}, "JSON");
    return result.workspaces;
}

export async function getWorkspaceFoldersWithChildIdsAndProjectUrls(workspaceId) {
    const result = await $.getJSON(`/query/getProjectGroups?workspaceId=${workspaceId}`);
    return result.groups || [];
}

export async function listUsersWithDatasetVisibility(datasetId, access) {
    const url = `/query/workspace/listUsersWithDatasetVisibility?dataset=${datasetId}&access=${access}`;
    const result = await $.getJSON(url);
    return result.users || [];
}

export async function getPendingWorkspaceInvites(workspaceId) {
    const result = await $.getJSON(`/query/getPendingWorkspaceInvites?workspaceId=${workspaceId}`);
    return result.invites;
}

export async function getInviteTokens(workspaceId) {
    const result = await $.getJSON("/query/inviteTokens", { workspaceId });

    return result.tokens;
}

export async function validateInviteToken(token) {
    const result = await $.post("/query/validateInviteToken", { token }, "JSON");

    return result;
}

export async function joinWorkspace(token, action) {
    const result = await $.post("/query/joinWorkspace", { token, action }, "JSON");

    return result;
}

export async function joinableWorkspaces() {
    const result = await $.getJSON("/query/joinableWorkspaces");

    return result.workspaces;
}

export async function canEnableJoinRequests(workspaceId) {
    const result = await $.getJSON("/query/canEnableJoinRequests", { workspaceId });

    return result.canEnable;
}

export async function setJoinable(workspaceId, joinable) {
    const result = await $.post("/query/setWorkspaceJoinable", { workspaceId, joinable }, "JSON");

    return result;
}

export async function getPendingJoinRequests(workspaceId) {
    const result = await $.getJSON("/query/listWorkspaceJoinRequests", { workspaceId });

    return result.requests;
}

export async function getUserPendingJoinRequests() {
    const result = await $.getJSON("/query/listUserWorkspaceJoinRequests");

    return result.requests;
}

export async function createWorkspaceJoinRequest(workspaceId) {
    const result = await $.post("/query/askToJoinWorkspace", { workspaceId }, "JSON");

    return result;
}

export async function respondToWorkspaceJoinRequest(workspaceId, requestId, role, response) {
    const result = await $.post(
        "/query/respondToWorkspaceJoinRequest",
        { workspaceId, requestId, role, response },
        "JSON"
    );

    return result;
}

export async function inviteToWorkspace({ emails, workspaceId, role, source }) {
    const data = { emails, workspaceId, role, source };
    const result = await $.post("/query/inviteToWorkspace", data, "JSON");
    return result;
}

export async function respondToWorkspaceInvite(workspaceId, action) {
    const data = { workspaceId, action };
    const result = $.post("/query/workspaceInviteRespond", data, "JSON");
    return result;
}

export async function getWorkspaceInvites() {
    const result = await $.getJSON("/query/workspaceInvites");
    return result?.invites || [];
}

export async function resolveWorkspaceIdsToUrls(workspaceIds) {
    return await $.post("/query/workspace/resolveIdsToUrls", { workspaceIds }, "JSON");
}

export async function canReceiveExpertConsultation(workspaceId) {
    const result = await $.getJSON("/query/canReceiveExpertConsultation", { workspaceId });

    return result;
}

export async function cancelPendingWorkspaceInvite(workspaceId, inviteIdToCancel) {
    const data = { workspaceId, inviteIdToCancel };
    const result = await $.post("/query/cancelPendingWorkspaceInvite", data, "JSON");
    return result;
}

export async function resendPendingWorkspaceInvite(workspaceId, inviteIdToResend) {
    const data = { workspaceId, inviteIdToResend };
    const result = await $.post("/query/resendPendingWorkspaceInvite", data, "JSON");
    return result;
}

export async function upgradeToResearchPlan(workspaceId, selectedPlan) {
    const data = { workspaceId, selectedPlan };
    const result = await $.post("/query/workspace/upgradeToResearchPlan", data, "JSON");
    return result;
}

export async function downgradeToSandboxPlan(workspaceId) {
    const data = { workspaceId };
    const result = await $.post("/query/workspace/downgradeToSandboxPlan", data, "JSON");
    return result;
}

export async function downgradeToPublicPlan(workspaceId) {
    const data = { workspaceId };
    const result = await $.post("/query/workspace/downgradeToPublicPlan", data, "JSON");
    return result;
}

export async function extendStarterTrial(workspaceId) {
    const data = { workspaceId };
    const result = await $.post("/query/workspace/extendStarterTrial", data, "JSON");
    return result;
}

export async function activatePremiumTrial(workspaceId, { funnelSource, feature } = {}) {
    const data = { workspaceId, funnelSource, feature };
    const result = await $.post("/query/workspace/activatePremiumTrial", data, "JSON");
    return result;
}

export async function getHasUsedTrial() {
    const result = await $.getJSON("/query/workspace/hasUsedTrial");
    return result.hasUsedTrial;
}

export async function fetchCheckoutUrlAndRedirect(data) {
    const result = await fetch("/query/checkout/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
    });
    if (result.ok) {
        const { url } = await result.json();
        try {
            window.open(url, "_blank");
        } catch (error) {
            window.location.href = url;
        }
    }
}

export async function updateUserRoleInWorkspace(workspaceId, userIdToChange, role) {
    const data = { workspaceId, userIdToChange, role };
    const result = await $.post("/query/updateUserRoleInWorkspace", data, "JSON");
    return result;
}

export async function removeUserFromWorkspace(workspaceId, userIdToRemove) {
    const data = { workspaceId, userIdToRemove };
    const result = await $.post("/query/deleteUserFromWorkspace", data, "JSON");
    return result;
}

export async function syncPrepaidInCentsWithUsage({
    workspaceId,
    skipCache = null,
    currentPrepaidInCents = null
}) {
    const data = { workspaceId, skipCache, currentPrepaidInCents };
    const result = await $.post("/query/workspace/syncPrepaidInCentsWithUsage", data, "JSON");
    return result;
}

export async function switchToUBP({ workspaceId }) {
    const data = { workspaceId };
    const result = await $.post("/query/workspace/switchToUBP", data, "JSON");
    return result;
}

export async function updateCreditCap({ workspaceId, creditCap }) {
    const data = { workspaceId, creditCap };
    const result = await $.post("/query/workspace/updateCreditCap", data, "JSON");
    return result;
}

export async function usageCycleAuditLog({ workspaceId, cycleStartSeconds, skipCache = false }) {
    const data = { cycleStartSeconds, workspaceId, skipCache };
    const result = await $.post("/query/workspace/usageCycleAuditLog", data);
    return result;
}

export async function updateCustomRoles(workspaceId, customRoles) {
    const response = await fetch("/query/workspace/updateCustomRoles", {
        method: "POST",
        body: JSON.stringify({ workspaceId, customRoles }),
        headers: { "Content-Type": "application/json" }
    });
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || "Failed to update custom roles");
    }
    return result;
}

export async function addAllowedBlock(workspaceId, blockType, allBlocks = null) {
    const body = { workspaceId, blockType };
    if (allBlocks) {
        body.allBlocks = allBlocks;
    }
    const response = await fetch("/query/workspace/allowedBlocks/add", {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" }
    });
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || "Failed to add allowed workflow block");
    }
    return result;
}

export async function removeAllowedBlock(workspaceId, blockType, allBlocks = null) {
    const body = { workspaceId, blockType };
    if (allBlocks) {
        body.allBlocks = allBlocks;
    }
    const response = await fetch("/query/workspace/allowedBlocks/remove", {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" }
    });
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || "Failed to remove allowed workflow block");
    }
    return result;
}

export async function updatePublicProjectsDisabled(workspaceId, publicProjectsDisabled) {
    const response = await fetch("/query/workspace/updatePublicProjectsDisabled", {
        method: "POST",
        body: JSON.stringify({ workspaceId, publicProjectsDisabled }),
        headers: { "Content-Type": "application/json" }
    });
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || "Failed to update public projects setting");
    }
    return result;
}

export async function renameWorkspace(workspaceId, newName, updateUrl) {
    const response = await fetch("/query/workspace/rename", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId, newName, updateUrl })
    });
    const result = await response.json();
    if (!response.ok) {
        throw new Error(result.error || "Failed to rename workspace");
    }
    return result;
}

export async function claimWalkthroughCredits(workspaceId, walkthroughId) {
    const response = await fetch("/query/workspace/walkthrough/claimCredits", {
        method: "POST",
        body: JSON.stringify({ workspaceId, walkthroughId }),
        headers: { "Content-Type": "application/json" }
    });
    if (!response.ok) {
        throw new Error("Failed to claim walkthrough credits");
    }
    return response.json();
}

/**
 * @param {string} workspaceId
 * @returns {Promise<{ eligible?: boolean, redeemed?: boolean }>}
 */
export async function getWalkthroughCreditsStatus(workspaceId) {
    const response = await fetch(
        `/query/workspace/walkthrough/creditsStatus?workspaceId=${encodeURIComponent(workspaceId)}`
    );
    if (!response.ok) {
        throw new Error("Failed to get walkthrough credits status");
    }
    return response.json();
}

export default {
    loadMyWorkspaces,
    getWorkspaceFoldersWithChildIdsAndProjectUrls,
    listUsersWithDatasetVisibility,
    getPendingWorkspaceInvites,
    inviteToWorkspace,
    respondToWorkspaceInvite,
    getInviteTokens,
    joinWorkspace,
    joinableWorkspaces,
    canEnableJoinRequests,
    setJoinable,
    getPendingJoinRequests,
    createWorkspaceJoinRequest,
    respondToWorkspaceJoinRequest,
    validateInviteToken,
    getWorkspaceInvites,
    cancelPendingWorkspaceInvite,
    resendPendingWorkspaceInvite,
    upgradeToResearchPlan,
    downgradeToSandboxPlan,
    downgradeToPublicPlan,
    extendStarterTrial,
    activatePremiumTrial,
    fetchCheckoutUrlAndRedirect,
    canReceiveExpertConsultation,
    resolveWorkspaceIdsToUrls,
    updateUserRoleInWorkspace,
    removeUserFromWorkspace,
    switchToUBP,
    updateCreditCap,
    usageCycleAuditLog,
    updateCustomRoles,
    addAllowedBlock,
    removeAllowedBlock,
    updatePublicProjectsDisabled,
    renameWorkspace,
    claimWalkthroughCredits,
    getWalkthroughCreditsStatus
};
